import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Card, Badge, fmtDate } from "./ui";
import { ArrowLeft, Download, RefreshCw, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";

const ORDER_STATUSES = ["pending", "pending_business_verification", "pending_payment", "paid", "failed", "canceled", "expired"];

export default function OrderDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => api.get(`/admin/orders/${id}`).then((r) => setD(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="text-white/50">Laden...</p>;
  const { order, verification, payment, invoice, messages } = d;

  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(apiErr(e)); } };
  const changeStatus = (s) => act(() => api.patch(`/admin/orders/${id}/status`, { status: s }), "Status bijgewerkt");
  const approveVerif = () => act(async () => { const { data } = await api.post(`/admin/orders/${id}/approve-verification`); if (data.checkout_url) toast.info("Betaallink aangemaakt"); }, "Verificatie goedgekeurd");
  const resendPay = () => act(() => api.post(`/admin/orders/${id}/resend-payment`), "Betaallink opnieuw verzonden");
  const addNote = () => { if (!note.trim()) return; act(() => api.post(`/admin/orders/${id}/note`, { text: note }), "Notitie toegevoegd"); setNote(""); };
  const download = async () => {
    try {
      const res = await api.get(`/admin/invoices/${invoice.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${invoice.invoice_number}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <Link to="/admin/orders" className="mb-4 inline-flex items-center gap-2 text-sm text-white/60 hover:text-white"><ArrowLeft size={16} /> Terug naar orders</Link>
      <PageHeader title={order.order_number} subtitle={`Aangemaakt ${fmtDate(order.created_at)}`}>
        <div className="flex items-center gap-2"><Badge status={order.status} /></div>
      </PageHeader>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="p-6">
            <h3 className="font-display text-lg text-white">Klant & levering</h3>
            <div className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              {[["Bedrijf", order.customer.company_name], ["KvK", order.customer.kvk_number], ["Contact", order.customer.contact_person], ["E-mail", order.customer.email], ["Telefoon", order.customer.phone], ["Factuuradres", order.customer.billing_address], ["Afleveradres", order.customer.delivery_address], ["Postcode/plaats", `${order.customer.postal_code} ${order.customer.city}`], ["Land", order.customer.country]].map(([k, v]) => (
                <div key={k}><span className="text-white/40">{k}: </span><span className="text-white/80">{v}</span></div>
              ))}
            </div>
            {order.notes && <p className="mt-4 rounded-xl bg-white/[0.03] p-3 text-sm text-white/60">Opmerking: {order.notes}</p>}
          </Card>

          <Card className="p-6">
            <h3 className="font-display text-lg text-white">Producten</h3>
            <table className="mt-4 w-full text-sm">
              <thead><tr className="text-white/40"><th className="text-left font-normal">Product</th><th className="text-right font-normal">Aantal</th><th className="text-right font-normal">Prijs excl.</th><th className="text-right font-normal">Totaal</th></tr></thead>
              <tbody>
                {order.products.map((p, i) => (
                  <tr key={i} className="border-t border-white/5 text-white/80"><td className="py-2">{p.name}</td><td className="py-2 text-right">{p.quantity}</td><td className="py-2 text-right">€{p.price_ex_vat.toFixed(2)}</td><td className="py-2 text-right">€{(p.price_ex_vat * p.quantity).toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 ml-auto w-56 space-y-1 border-t border-white/10 pt-3 text-sm text-white/70">
              <div className="flex justify-between"><span>Subtotaal</span><span>€{order.totals.subtotal_ex_vat.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Btw</span><span>€{order.totals.vat_amount.toFixed(2)}</span></div>
              <div className="flex justify-between font-display text-white"><span>Totaal</span><span>€{order.totals.total_inc_vat.toFixed(2)}</span></div>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-display text-lg text-white">Bedrijfsverificatie</h3>
            {verification ? (
              <>
                <div className="mt-3 flex items-center gap-3"><Badge status={verification.overall_status} /><span className="text-xs text-white/40">Correlation: {verification.correlation_id?.slice(0, 8)}</span></div>
                <div className="mt-4 space-y-2">
                  {verification.checks?.map((c, i) => (
                    <div key={i} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-xs">
                      <span className="text-white/70">{c.type.replace(/_/g, " ")}</span>
                      <span className="flex items-center gap-2"><span className="text-white/40">{c.source}</span><Badge status={c.result === "pass" ? "approved" : c.result === "fail" ? "rejected" : "manual_review_required"} /></span>
                    </div>
                  ))}
                </div>
              </>
            ) : <p className="mt-3 text-sm text-white/40">Geen verificatie</p>}
          </Card>

          <Card className="p-6">
            <h3 className="font-display text-lg text-white">Communicatiehistorie</h3>
            <div className="mt-4 space-y-2">
              {messages?.length ? messages.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-lg bg-white/[0.03] px-3 py-2 text-xs">
                  <span className="text-white/70">{m.channel === "email" ? "✉" : "WA"} {m.template} → {m.recipient}</span>
                  <span className="flex items-center gap-2 text-white/40">{fmtDate(m.created_at)}<Badge status={m.status} /></span>
                </div>
              )) : <p className="text-sm text-white/40">Geen berichten</p>}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-display text-lg text-white">Betaling</h3>
            {payment ? (
              <div className="mt-3 space-y-2 text-sm text-white/70">
                <div className="flex justify-between"><span>Status</span><Badge status={payment.status} /></div>
                <div className="flex justify-between"><span>Bedrag</span><span>€{payment.amount.toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Provider</span><span>{payment.provider}</span></div>
                {payment.checkout_url && <a href={payment.checkout_url} target="_blank" rel="noopener noreferrer" className="mt-2 block truncate text-xs text-sfc-cyan hover:underline">Betaallink openen</a>}
              </div>
            ) : <p className="mt-3 text-sm text-white/40">Nog geen betaling</p>}
          </Card>

          <Card className="p-6">
            <h3 className="font-display text-lg text-white">Acties</h3>
            <div className="mt-4 space-y-2">
              {verification?.overall_status === "manual_review_required" && (
                <button data-testid="approve-verification-btn" onClick={approveVerif} className="flex w-full items-center justify-center gap-2 rounded-full bg-emerald-500/20 px-4 py-2.5 text-sm text-emerald-300 hover:bg-emerald-500/30"><CheckCircle2 size={16} /> Verificatie goedkeuren</button>
              )}
              <button data-testid="resend-payment-btn" onClick={resendPay} className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white/80 hover:bg-white/10"><RefreshCw size={16} /> Betaallink opnieuw</button>
              {invoice && <button data-testid="download-invoice-btn" onClick={download} className="flex w-full items-center justify-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white/80 hover:bg-white/10"><Download size={16} /> Factuur {invoice.invoice_number}</button>}
              <select data-testid="order-status-select" value={order.status} onChange={(e) => changeStatus(e.target.value)} className="w-full rounded-full border border-white/10 bg-sfc-night px-4 py-2.5 text-sm text-white focus:outline-none">
                {ORDER_STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-display text-lg text-white">Interne notities</h3>
            <div className="mt-3 space-y-2">
              {order.internal_notes?.map((n, i) => <p key={i} className="rounded-lg bg-white/[0.03] p-2 text-xs text-white/60">{n.text}<br /><span className="text-white/30">{n.author} · {fmtDate(n.at)}</span></p>)}
            </div>
            <div className="mt-3 flex gap-2">
              <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notitie..." className="flex-1 rounded-full border border-white/10 bg-sfc-night px-3 py-2 text-sm text-white focus:outline-none" />
              <button data-testid="add-note-btn" onClick={addNote} className="rounded-full bg-sfc-orange/20 p-2 text-sfc-orange"><Send size={16} /></button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
